import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Flame, RefreshCw, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const SOURCE_LABELS = {
  "/": "HVAC (home)",
  "/plumbers": "Plumbers",
  "/electricians": "Electricians",
  "/roofers": "Roofers",
  "/contractors": "General Contractors",
  "/compare": "Compare page",
  "/resources": "Resources page",
};

const sourceLabel = (s) => SOURCE_LABELS[s] || s || "—";

export default function WarmLeadsPanel({ token }) {
  const [reports, setReports] = useState(null);
  const [chatLeads, setChatLeads] = useState(null);
  const [crmInterest, setCrmInterest] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };
  const load = () => {
    axios
      .get(`${API}/calculator/reports`, { headers })
      .then((r) => setReports(r.data))
      .catch(() => setReports([]));
    axios
      .get(`${API}/chat/leads`, { headers })
      .then((r) => setChatLeads(r.data))
      .catch(() => setChatLeads([]));
    axios
      .get(`${API}/admin/crm-interest`, { headers })
      .then((r) => setCrmInterest(r.data))
      .catch(() => setCrmInterest({ ranked: [] }));
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="warm-leads-panel">
      {crmInterest && crmInterest.ranked && crmInterest.ranked.length > 0 && (
        <div className="mb-5 rounded-xl border border-white/[0.08] bg-[#12161F] p-5" data-testid="crm-interest-card">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            CRM interest — what prospects say they run (demo forms + chat)
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {crmInterest.ranked.map((r, i) => (
              <span
                key={r.crm}
                data-testid={`crm-interest-row-${i}`}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-medium ${
                  i === 0
                    ? "border-[#F25F22]/50 bg-[#F25F22]/10 text-[#F25F22]"
                    : "border-white/[0.1] bg-white/[0.03] text-slate-300"
                }`}
              >
                {r.crm} · {r.count}
                <span className="ml-1.5 text-[10px] text-slate-500">
                  ({r.demo_requests} form / {r.chat_mentions} chat)
                </span>
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-white/[0.08] bg-[#12161F] p-5">
        <Flame className="h-5 w-5 text-[#F25F22]" />
        <p className="text-sm text-slate-300">
          <span className="font-semibold text-slate-100">Warm leads</span> — visitors who emailed themselves a
          revenue-leak report but haven&apos;t booked a demo yet
        </p>
        <button
          onClick={load}
          data-testid="warm-leads-refresh"
          className="ml-auto rounded-md border border-white/[0.12] p-2 text-slate-400 hover:text-slate-100"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-5 overflow-x-auto rounded-xl border border-white/[0.08] bg-[#12161F]">
        {reports === null ? (
          <div className="flex items-center justify-center gap-2 p-16 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
          </div>
        ) : reports.length === 0 ? (
          <div className="flex flex-col items-center gap-3 p-16 text-slate-500" data-testid="warm-leads-empty">
            <Flame className="h-8 w-8" />
            <p className="text-sm">No calculator reports yet — they appear here the moment a visitor emails themselves one.</p>
          </div>
        ) : (
          <table className="w-full min-w-[820px] text-left text-sm" data-testid="warm-leads-table">
            <thead>
              <tr className="border-b border-white/[0.08] font-mono text-[10px] uppercase tracking-widest text-slate-500">
                {["Email", "Missed calls/wk", "Avg ticket", "Annual leak", "Recoverable", "Source", "Date", ""].map((h) => (
                  <th key={h} className="px-5 py-4 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} data-testid={`warm-lead-${r.id}`} className="border-b border-white/[0.05] last:border-0 hover:bg-white/[0.02]">
                  <td className="px-5 py-3.5 font-medium text-slate-100" data-testid={`warm-lead-email-${r.id}`}>{r.email}</td>
                  <td className="px-5 py-3.5 text-slate-300">{r.missed_calls}</td>
                  <td className="px-5 py-3.5 text-slate-300">{money(r.avg_ticket)}</td>
                  <td className="px-5 py-3.5 font-mono text-red-400">{money(r.annual_leak)}</td>
                  <td className="px-5 py-3.5 font-mono text-emerald-400">{money(r.recoverable)}</td>
                  <td className="px-5 py-3.5">
                    <span className="rounded-full border border-[#F25F22]/30 bg-[#F25F22]/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-[#F25F22]">
                      {sourceLabel(r.source)}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-slate-500">
                    {new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <a
                      href={`mailto:${r.email}?subject=Your Backfill revenue report&body=Hi — saw you ran the numbers on missed calls. Happy to walk you through how Backfill recovers that revenue on a quick demo.`}
                      data-testid={`warm-lead-mailto-${r.id}`}
                      title="Email this lead"
                      className="inline-flex rounded-md p-2 text-slate-500 transition-colors hover:bg-[#F25F22]/10 hover:text-[#F25F22]"
                    >
                      <Mail className="h-4 w-4" />
                    </a>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Delete the report for ${r.email}?`)) return;
                        try {
                          await axios.delete(`${API}/admin/warm-leads/calculator/${r.id}`, { headers });
                          load();
                        } catch {
                          toast.error("Could not delete");
                        }
                      }}
                      data-testid={`warm-lead-delete-${r.id}`}
                      title="Delete"
                      className="ml-1 inline-flex rounded-md p-2 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {chatLeads !== null && chatLeads.length > 0 && (
        <div className="mt-5 overflow-hidden rounded-xl border border-white/[0.08] bg-[#12161F]" data-testid="chat-leads-section">
          <p className="border-b border-white/[0.08] px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            Chat + playground captures — visitors who asked for a follow-up
          </p>
          {chatLeads.map((l) => (
            <div key={l.id} data-testid={`chat-lead-${l.id}`} className="flex items-center gap-4 border-b border-white/[0.05] px-5 py-3 last:border-0">
              <span className="text-sm font-medium text-slate-100">{l.email}</span>
              <span className="text-xs text-slate-500">
                {l.kind === "playground" ? "playground" : `${l.turns} messages`}{l.company_name ? ` · ${l.company_name}` : ""}
              </span>
              <span className="text-xs text-slate-500">
                {new Date(l.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </span>
              <a
                href={`mailto:${l.email}?subject=Your Backfill questions&body=Hi — saw your questions on our site chat. Happy to set up a quick walkthrough whenever suits you.`}
                data-testid={`chat-lead-mailto-${l.id}`}
                title="Email this lead"
                className="ml-auto rounded-md p-2 text-slate-500 transition-colors hover:bg-[#F25F22]/10 hover:text-[#F25F22]"
              >
                <Mail className="h-4 w-4" />
              </a>
              <button
                onClick={async () => {
                  if (!window.confirm(`Delete the capture for ${l.email}?`)) return;
                  try {
                    await axios.delete(`${API}/admin/warm-leads/chat/${l.id}`, { headers });
                    load();
                  } catch {
                    toast.error("Could not delete");
                  }
                }}
                data-testid={`chat-lead-delete-${l.id}`}
                title="Delete"
                className="rounded-md p-2 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-400"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
